import { useDispatch, useSelector } from 'react-redux';
import { getValue } from '@Selectors';
import { useConfigurationsSearch } from '@Hooks';
import { resampleSensorJob, resampleSensor } from '@Actions';
import { ResampleFormData } from '@Components';
import { SensorTypeFilter } from '@Types/configurations.types';
import { ResampleJobRequest } from '@Types/sensorResample';

export type ResampleJobFnRequest = {
	formData: ResampleFormData;
	sensorId: string;
	sensorIds?: string[];
};

function createSensorQuery(
	searchValue: string,
	sensorTypes: SensorTypeFilter[],
	dataSourceTypes?: string[],
) {
	const parsedSensorTypes = sensorTypes.includes('All')
		? undefined
		: sensorTypes?.join(',');
	const parsedDataSourceTypes = dataSourceTypes?.includes('All')
		? undefined
		: dataSourceTypes?.join(',');
	return {
		sensorId: searchValue,
		sensorAlias: searchValue,
		sensorTypes: parsedSensorTypes,
		dataSourceTypes: parsedDataSourceTypes,
	};
}

interface UseResampleJobsReturn {
	resample: (request: ResampleJobFnRequest) => void;
	issueResampleJob: (request: ResampleJobFnRequest) => void;
}

export const useResampleJobs = (): UseResampleJobsReturn => {
	const dispatch = useDispatch();

	const {
		configurations: {
			checkOrUncheckedRows,
			dataSourceTypes: dsTypes,
			headerCheckboxChecked,
			sensorTypes,
		},
	} = useSelector(getValue);

	const { searchValue } = useConfigurationsSearch();

	function remapRows() {
		return checkOrUncheckedRows.map(el => {
			const isSensorId = typeof el === 'string'; // ! Important: This case comes from NEW_MUIX_TABLE
			return isSensorId ? el : el.sensorId;
		});
	}

	function issueResampleJob(request: ResampleJobFnRequest) {
		const { formData, sensorId, sensorIds } = request;

		const payload: ResampleJobRequest = {
			defaultIncluded: headerCheckboxChecked,
			deltaTimeSeconds: formData.deltaTimeSeconds,
			endTime: formData.endTime,
			sensorIds: [],
			sensorQuery: createSensorQuery(searchValue, sensorTypes, dsTypes),
			startTime: formData.startTime,
		};

		if (headerCheckboxChecked && checkOrUncheckedRows?.length) {
			payload.sensorIds = remapRows(); // ? all but some unselected
		} else if (sensorIds?.length) {
			payload.sensorIds = sensorIds; // ? some unselected
		} else {
			payload.sensorIds = [sensorId]; // ? single
		}

		dispatch(resampleSensorJob(payload));
	}

	function resample(request: ResampleJobFnRequest): void {
		const { formData, sensorId } = request;
		const { startTime: startDateTime, endTime: endDateTime } = formData;
		const payload = { sensorId, startDateTime, endDateTime };
		dispatch(resampleSensor(payload));
	}

	return { resample, issueResampleJob };
};
